import { useCallback, useEffect, useState } from "react";
import { Search, CheckCircle2, AlertTriangle } from "lucide-react";
import adminService from "../../../../services/adminService";

const USER_TYPE_TABS = [
  { key: "", label: "Todos" },
  { key: "Company", label: "Empresas" },
  { key: "Freelancer", label: "Freelancers" },
  { key: "Admin", label: "Admin" },
];

const USER_TYPE_LABELS = {
  Company: "Empresa",
  Freelancer: "Freelancer",
  Admin: "Admin",
};

function formatCurrency(value) {
  return (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// Saldo de cada usuário, com conferência contra o histórico de movimentações.
export default function WalletBalances({ onSelectUser }) {
  const [userType, setUserType] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);
  const [onlyMismatch, setOnlyMismatch] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getWalletBalances({
        userType: userType || undefined,
        search: appliedSearch || undefined,
        onlyWithBalance,
        onlyMismatch,
      });
      setData(result);
    } catch (error) {
      console.error("Erro ao carregar saldos:", error);
    } finally {
      setLoading(false);
    }
  }, [userType, appliedSearch, onlyWithBalance, onlyMismatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
  };

  return (
    <>
      <div className="admin-finance__filters">
        <div className="admin-finance__tabs">
          {USER_TYPE_TABS.map((t) => (
            <button
              key={t.key || "all"}
              className={userType === t.key ? "active" : ""}
              onClick={() => setUserType(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form className="admin-finance__search" onSubmit={handleSearch}>
          <Search size={14} />
          <input
            placeholder="Nome ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>

        <label className="admin-finance__check">
          <input
            type="checkbox"
            checked={onlyWithBalance}
            onChange={(e) => setOnlyWithBalance(e.target.checked)}
          />
          Só com saldo
        </label>

        <label className="admin-finance__check">
          <input
            type="checkbox"
            checked={onlyMismatch}
            onChange={(e) => setOnlyMismatch(e.target.checked)}
          />
          Só divergências
        </label>
      </div>

      {data && (
        <div className="admin-finance__totals">
          <span>{data.count} carteira(s)</span>
          <span>Total: <strong>{formatCurrency(data.totalBalance)}</strong></span>
          <span>Reservado: <strong>{formatCurrency(data.totalReserved)}</strong></span>
          <span className={data.mismatchCount > 0 ? "admin-finance__warn" : "admin-finance__ok"}>
            {data.mismatchCount > 0
              ? `${data.mismatchCount} com divergência`
              : "Todos os saldos conferem"}
          </span>
        </div>
      )}

      <div className="admin-finance__table">
        <div className="admin-finance__head admin-finance__head--balances">
          <span>Usuário</span>
          <span>Saldo total</span>
          <span>Reservado</span>
          <span>Disponível</span>
          <span>Pelo histórico</span>
          <span>Conferência</span>
        </div>

        {loading && <div className="admin-loading">Carregando saldos...</div>}

        {!loading && data?.items.length === 0 && (
          <div className="admin-finance__empty">Nenhuma carteira encontrada.</div>
        )}

        {!loading && data?.items.map((w) => {
          const ok = w.balanceMatches && w.reservedMatches;

          return (
            <div
              key={w.userId}
              className={`admin-finance__row admin-finance__row--balances ${ok ? "" : "admin-finance__row--mismatch"}`}
            >
              <span title={w.userEmail}>
                <button
                  className="admin-finance__link"
                  onClick={() => onSelectUser?.(w)}
                  title="Ver movimentações deste usuário"
                >
                  {w.userName}
                </button>
                <small>{USER_TYPE_LABELS[w.userType] ?? w.userType} · {w.userEmail}</small>
              </span>

              <span className="admin-finance__strong">{formatCurrency(w.balance)}</span>
              <span>{formatCurrency(w.reserved)}</span>
              <span className="admin-finance__strong">{formatCurrency(w.available)}</span>

              <span className={w.balanceMatches ? "" : "admin-finance__warn"}>
                {formatCurrency(w.ledgerBalance)}
              </span>

              <span className="admin-finance__check-cell">
                {ok ? (
                  <span className="admin-finance__ok">
                    <CheckCircle2 size={14} /> Confere
                  </span>
                ) : (
                  <>
                    {!w.balanceMatches && (
                      <span className="admin-finance__warn">
                        <AlertTriangle size={14} />
                        Saldo difere em {formatCurrency(w.balance - w.ledgerBalance)}
                      </span>
                    )}
                    {!w.reservedMatches && (
                      <span className="admin-finance__warn">
                        <AlertTriangle size={14} />
                        Reservado {formatCurrency(w.reserved)}, esperado {formatCurrency(w.expectedReserved)}
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}
